import type { FeatureExtractionPipeline } from '@huggingface/transformers';

// Quantized (q8) ONNX build of all-MiniLM-L6-v2 — a small, well-known
// sentence-embedding model. `@huggingface/transformers` itself bundles a
// ~23MB ONNX Runtime WASM backend, so it's dynamically imported only when
// warmEmbedder() actually runs (only if A3 is enabled — see main.ts) rather
// than as a static top-level import, which would otherwise pull that weight
// into every user's initial bundle whether they use A3 or not. The model
// weights are fetched from the Hugging Face Hub on first use (not bundled at
// all), then cached by the browser/transformers.js's own cache.
const MODEL_ID = 'onnx-community/all-MiniLM-L6-v2-ONNX';

let extractorPromise: Promise<FeatureExtractionPipeline> | null = null;
let ready = false;

/** Kick off loading the model in the background — call this only once A3 is known to be enabled (see main.ts), not unconditionally, so the ~23MB runtime is never fetched for users who've turned A3 off. */
export function warmEmbedder(): void {
  if (extractorPromise) return;
  const promise: Promise<FeatureExtractionPipeline> = import('@huggingface/transformers')
    .then(({ pipeline }) => pipeline('feature-extraction', MODEL_ID, { dtype: 'q8' }))
    .then((extractor) => {
      ready = true;
      return extractor;
    })
    .catch((err) => {
      if (extractorPromise === promise) extractorPromise = null; // allow retrying later (e.g. after a network blip)
      throw err;
    });
  extractorPromise = promise;
  // warmEmbedder() is fire-and-forget (main.ts never awaits or attaches a .catch() to its
  // result) — embedText() still awaits/rethrows the real rejection above when A3 actually needs
  // it, but if the load fails and nothing ever calls embedText() (e.g. the user never sends a
  // message, or A3 gets disabled first), that rejection would otherwise surface as an unhandled
  // promise rejection. This silently observes it without changing what embedText() sees.
  promise.catch(() => {});
}

export function isEmbedderReady(): boolean {
  return ready;
}

/** Embeds a single string. Throws if the model isn't loaded — callers (the A3 stage) should check `isEmbedderReady()` first and fail open rather than await a cold load. */
export async function embedText(text: string): Promise<Float32Array> {
  if (!extractorPromise) throw new Error('Embedder not warmed yet — call warmEmbedder() first.');
  const extractor = await extractorPromise;
  const output = await extractor(text, { pooling: 'mean', normalize: true });
  return output.data as Float32Array;
}

export function cosineSimilarity(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}
